import { useEffect, useState, useMemo, useCallback } from 'react';

interface Category { id: number; label: string; icon: string | null; sort_order: number; }
interface Entry {
  id: number; category_id: number; group_label: string | null;
  label: string; value: string | null; note: string | null; sort_order: number;
}

function CopyRow({ entry }: { entry: Entry }) {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(() => {
    const text = entry.value ?? entry.label;
    void navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1100);
    });
  }, [entry]);
  return (
    <button type="button" className="ref-entry" onClick={copy} title="click to copy">
      <span className="ref-entry-label">{entry.label}</span>
      {entry.value ? <span className="ref-entry-value">{entry.value}</span> : null}
      {entry.note ? <span className="ref-entry-note">{entry.note}</span> : null}
      <span className={'ref-copy' + (copied ? ' ref-copy-on' : '')}>{copied ? 'copied' : 'copy'}</span>
    </button>
  );
}

export function RefPage() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<Set<number>>(new Set());
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch('/api/ref', { credentials: 'same-origin' });
        if (!resp.ok) throw new Error('HTTP ' + resp.status);
        const json = await resp.json();
        if (cancelled) return;
        setCategories(json.categories || []);
        setEntries(json.entries || []);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const q = query.trim().toLowerCase();
  const matches = useCallback((e: Entry) => {
    if (!q) return true;
    return [e.label, e.value, e.note, e.group_label].some(
      (s) => s && s.toLowerCase().includes(q),
    );
  }, [q]);

  // Group entries by category, then by group_label (null group = flat rows).
  const byCategory = useMemo(() => {
    const map = new Map<number, { flat: Entry[]; groups: Map<string, Entry[]> }>();
    for (const c of categories) map.set(c.id, { flat: [], groups: new Map() });
    for (const e of entries) {
      if (q && !matches(e)) continue;
      const bucket = map.get(e.category_id);
      if (!bucket) continue;
      if (e.group_label) {
        if (!bucket.groups.has(e.group_label)) bucket.groups.set(e.group_label, []);
        bucket.groups.get(e.group_label)!.push(e);
      } else {
        bucket.flat.push(e);
      }
    }
    return map;
  }, [categories, entries, q, matches]);

  const toggle = (id: number) =>
    setOpen((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleGroup = (key: string) =>
    setOpenGroups((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });

  const countFor = (id: number) => {
    const b = byCategory.get(id);
    if (!b) return 0;
    return b.flat.length + Array.from(b.groups.values()).reduce((s, g) => s + g.length, 0);
  };

  return (
    <div className="ref-shell">
      <header className="ref-header">
        <h1>Reference</h1>
        <input
          className="ref-search"
          type="text"
          placeholder="search everything"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </header>

      {loading ? <div className="ref-status">loading</div> : null}
      {error ? <div className="ref-status ref-error">error: {error}</div> : null}

      <div className="ref-cats">
        {categories.map((c) => {
          const bucket = byCategory.get(c.id);
          const count = countFor(c.id);
          // When searching, auto-open categories that have matches.
          const isOpen = q ? count > 0 : open.has(c.id);
          if (q && count === 0) return null;
          return (
            <section key={c.id} className="ref-cat">
              <button type="button" className="ref-cat-head" onClick={() => toggle(c.id)}>
                <span className={'ref-chevron' + (isOpen ? ' ref-chevron-open' : '')}>›</span>
                <span className="ref-cat-label">{c.label}</span>
                <span className="ref-cat-count">{count}</span>
              </button>
              {isOpen && bucket ? (
                <div className="ref-cat-body">
                  {Array.from(bucket.groups.entries()).map(([gname, gentries]) => {
                    const gkey = c.id + '::' + gname;
                    const gOpen = q ? true : openGroups.has(gkey);
                    return (
                      <div key={gkey} className="ref-group">
                        <button type="button" className="ref-group-head" onClick={() => toggleGroup(gkey)}>
                          <span className={'ref-chevron' + (gOpen ? ' ref-chevron-open' : '')}>›</span>
                          <span className="ref-group-label">{gname}</span>
                          <span className="ref-cat-count">{gentries.length}</span>
                        </button>
                        {gOpen ? (
                          <div className="ref-group-body">
                            {gentries.map((e) => <CopyRow key={e.id} entry={e} />)}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                  {bucket.flat.map((e) => <CopyRow key={e.id} entry={e} />)}
                  {count === 0 ? <div className="ref-empty">empty</div> : null}
                </div>
              ) : null}
            </section>
          );
        })}
      </div>
    </div>
  );
}

export default RefPage;
